import React, { useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import {
  CheckOperator,
  MemoryDTO,
  MemoryLinkValue,
  MemoryShowIn,
  MemoryType,
  MemoryValue,
  StepCheckOperatorType,
} from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { Character } from 'dorian-shared/types/character/Character';
import {
  getMemorySlotValue,
  getMemorySlotVariableLink,
  isMemorySlotVariableLink,
} from '../../../../utils/choiceMemoryUtils';
import { MemorySlotCompareValueForm } from './MemorySlotCompareValueForm';
import { MemorySlotCompareVariableForm } from './MemorySlotCompareVariableForm';

const MORE_FUNCTIONS_ONLY_OPERATORS = new Set([
  StepCheckOperatorType.Min,
  StepCheckOperatorType.Max,
  StepCheckOperatorType.ConditionList,
  StepCheckOperatorType.PlayerHas,
]);

/** Operators that only appear when the slot type is Number (excludes Equal and NotEqual) */
const NUMBER_ONLY_OPERATORS = new Set([
  StepCheckOperatorType.Greater,
  StepCheckOperatorType.AtLeast,
  StepCheckOperatorType.Less,
  StepCheckOperatorType.AtMost,
]);

export type MemoryBankCheckValue = {
  name: string,
  /**
   * Check value depends on context:
   * - `MemoryValue` (string | number) — literal value for single checks
   * - `MemorySingleLink` — variable-to-variable comparison in single checks
   * - `MemoryArrayLink` — array of variable IDs for Min/Max multi-memory checks
   * - `MemoryShowIn[]` — visibility settings (showIn field)
   * - `number[]` — reward IDs for PlayerHas checks
   * - `null` — no value set
   */
  value: MemoryValue | MemoryLinkValue | MemoryShowIn[] | number[] | null,
}

interface MemorySlotCompareFormProp {
  inputNamePrefix: string,
  variableInputValue: number,
  operatorInputValue: string,
  checkInputValue: MemoryValue | MemoryLinkValue,
  currentSlot?: MemoryDTO,
  memoryBankSlots?: MemoryDTO[],
  stepIndex: number,
  characters: Character[],
  onChangeValue: (
    values: MemoryBankCheckValue[],
    stepIndex: number,
  ) => void,
  isDisabled?: boolean,
}

export function MemorySlotCompareForm(props: MemorySlotCompareFormProp) {
  const {
    inputNamePrefix,
    variableInputValue,
    operatorInputValue,
    checkInputValue,
    currentSlot,
    memoryBankSlots,
    stepIndex,
    characters,
    onChangeValue,
    isDisabled,
  } = props;

  const initialIsVariableCompare = isMemorySlotVariableLink(checkInputValue);
  const [isVariableCompare, setIsVariableCompare] = useState<boolean>(initialIsVariableCompare);

  const variables = memoryBankSlots?.filter(
    (slot) => slot.type === currentSlot?.type && slot.id !== currentSlot?.id,
  );

  if (!variables) {
    return null;
  }

  return (
    <Row xs={1} sm={2} lg={3}>
      <Form.Group as={Row}>
        <Col lg={4}>
          <Form.Label column>
            Whether
          </Form.Label>
        </Col>
        <Col>
          <Form.Control
            disabled={isDisabled}
            size="sm"
            as="select"
            name={`${inputNamePrefix}[variableId]`}
            value={variableInputValue}
            onChange={(event) => {
              const values: MemoryBankCheckValue[] = [
                {
                  name: 'variableId',
                  value: Number(event.target.value),
                },
              ];
              const newMemory = memoryBankSlots?.find(
                (slot) => slot.id === Number(event.target.value),
              );

              const isFromBooleanType = newMemory?.type !== MemoryType.Boolean
                && currentSlot?.type === MemoryType.Boolean;
              const isToBooleanType = newMemory?.type === MemoryType.Boolean
                && currentSlot?.type !== MemoryType.Boolean;

              if (isFromBooleanType || isToBooleanType) {
                values.push({
                  name: 'value',
                  value: '',
                });
              }
              const isEqualOrUnset = operatorInputValue === StepCheckOperatorType.Equal || !operatorInputValue;
              const isNumberOnlyOperator = NUMBER_ONLY_OPERATORS.has(operatorInputValue as StepCheckOperatorType);
              if (newMemory?.type === MemoryType.Number && isEqualOrUnset) {
                values.push({
                  name: 'operator',
                  value: StepCheckOperatorType.AtLeast,
                });
              } else if ((!newMemory || newMemory.type !== MemoryType.Number) && isNumberOnlyOperator) {
                values.push({
                  name: 'operator',
                  value: StepCheckOperatorType.Equal,
                });
              }
              onChangeValue(values, stepIndex);
            }}
          >
            <option />
            {memoryBankSlots?.map((slot) => (
              <option key={slot.id} value={slot.id}>
                {slot.name}
              </option>
            ))}
          </Form.Control>
        </Col>
      </Form.Group>
      <Form.Group as={Row}>
        <Col lg="auto">
          <Form.Label column>
            is
          </Form.Label>
        </Col>
        <Col className="mr-3">
          <Form.Control
            disabled={isDisabled}
            size="sm"
            as="select"
            name={`${inputNamePrefix}[operator]`}
            value={operatorInputValue}
            onChange={(event) => onChangeValue([{
              name: 'operator',
              value: event.target.value,
            }], stepIndex)}
          >
            {Object.entries(CheckOperator).map(([operatorKey, operatorValue]) => {
              if (MORE_FUNCTIONS_ONLY_OPERATORS.has(operatorKey as StepCheckOperatorType)) {
                return null;
              }
              const isSlotANumber = currentSlot?.type === MemoryType.Number;
              const isOnlyNumberOperator = operatorKey !== StepCheckOperatorType.Equal
                && operatorKey !== StepCheckOperatorType.NotEqual;

              if (!isSlotANumber && isOnlyNumberOperator) {
                return null;
              }
              return (
                <option key={`option${operatorKey}`} value={operatorKey}>
                  {operatorValue.title}
                </option>
              );
            })}
          </Form.Control>
        </Col>
      </Form.Group>
      <Form.Group as={Row}>
        <Col lg="auto" className="mt-1">
          <Form.Check
            custom
            id={`${inputNamePrefix}[isVariableCompare]`}
            disabled={isDisabled}
            checked={isVariableCompare}
            onChange={(event) => {
              setIsVariableCompare(event.target.checked);
            }}
            type="checkbox"
            label="use memory"
          />
        </Col>
        {isVariableCompare && (
        <Col>
          <MemorySlotCompareVariableForm
            valueInputValue={getMemorySlotVariableLink(checkInputValue)}
            stepIndex={stepIndex}
            onChangeValue={onChangeValue}
            inputName={`${inputNamePrefix}[value]`}
            isDisabled={isDisabled}
            variables={variables}
          />
        </Col>
        )}
        {!isVariableCompare && (
        <Col>
          <MemorySlotCompareValueForm
            onChangeValue={onChangeValue}
            stepIndex={stepIndex}
            valueInputValue={getMemorySlotValue(checkInputValue)}
            characters={characters}
            memoryBankSlot={currentSlot}
            inputName={`${inputNamePrefix}[value]`}
            isDisabled={isDisabled}
          />
        </Col>
        )}
      </Form.Group>
    </Row>
  );
}
